import React, { useState } from 'react';
import { Box, Button, MobileStepper, Step, StepLabel, Stepper } from '@mui/material';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import FooterWrapper from '@tenant/core/AppLayout/components/AppFooter/FooterWrapper';
import { ROUTE_STRING } from 'constants/RouteString';
import useCreatePipeline from 'redux/slices/pipelines/hooks/useCreatePipeline';
import useGetListPipelines from 'redux/slices/pipelines/hooks/useGetListPipelines';
import { sourceImageStepSchema, targetContainerStepSchema, nameStepSchema } from 'redux/slices/pipelines/constants/validationSchema';
import SourceImageStep from './steps/SourceImageStep';
import TargetContainerStep from './steps/TargetContainerStep';
import NameStep from './steps/NameStep';
import RunStep from './steps/RunStep';

const STEPS = [
	{ key: 'source', label: 'Source' },
	{ key: 'target', label: 'Target' },
	{ key: 'name', label: 'Name' },
	{ key: 'run', label: 'Run' }
];

// Yup schema for each of the first 3 (Formik-backed) steps. Step 4 (Run) is handled by
// PipelineRunForm's own local state/validation, not Formik (AC-8: the PAT must never
// be a Formik-managed value that could linger in form state after submit).
const STEP_SCHEMAS = [sourceImageStepSchema, targetContainerStepSchema, nameStepSchema];

const initialValues = {
	ImageName: '',
	AppInstanceId: '',
	ContainerAppId: '',
	Name: ''
};

// Create-only setup wizard (OQ-2): pick source image -> pick target (App Instance ->
// container) -> name -> run (AC-5). A single Formik instance backs steps 1-3 so Back/Next
// retains every previously entered value (AC-6/AC-7/AC-10 "retained" assertions); step 3's
// submit IS the create call (AC-7), and step 4 is only reachable after a successful create.
const PipelineWizard = () => {
	const navigate = useNavigate();
	const [activeStepIndex, setActiveStepIndex] = useState(0);
	const [createdPipeline, setCreatedPipeline] = useState(null);
	const [createError, setCreateError] = useState(null);

	const createMutate = useCreatePipeline();
	// Ensures the grouped pipeline list (used for 1:1 exclusion in step 2) is loaded even
	// if the wizard is opened directly rather than navigated to from the list page.
	useGetListPipelines();

	const isLastFormikStep = activeStepIndex === 2;
	const currentSchema = STEP_SCHEMAS[activeStepIndex];

	const handleBack = () => {
		setCreateError(null);
		setActiveStepIndex(i => Math.max(0, i - 1));
	};

	const handleCancel = () => {
		navigate(ROUTE_STRING.PIPELINE.LIST);
	};

	const handleRunTriggered = run => {
		// AC-9: on a successful trigger, transition into the live status view.
		// `replace` — the wizard has already created the pipeline and triggered the run, so it
		// must not stay on the history stack: pressing back on the status view would otherwise
		// drop the user into a spent wizard offering to create the thing they just created.
		navigate(ROUTE_STRING.PIPELINE.RUN_VIEW.replace(':id', createdPipeline.Id).replace(':runId', run.Id), {
			replace: true
		});
	};

	const handleSubmitStep = async (values, { setSubmitting }) => {
		if (!isLastFormikStep) {
			setActiveStepIndex(i => i + 1);
			setSubmitting(false);
			return;
		}

		// Step 3 (Name) submit = the actual create call (AC-7).
		setCreateError(null);
		try {
			const { data } = await createMutate.mutateAsync({
				Name: values.Name.trim(),
				ImageName: values.ImageName,
				ContainerAppId: Number(values.ContainerAppId)
			});
			setCreatedPipeline(data);
			setActiveStepIndex(3);
		} catch (error) {
			const status = error?.response?.status;
			setCreateError(
				status === 400
					? error?.response?.data?.message ??
							'This container may already have a live pipeline, or the name/details are invalid — pick a different target or name and try again.'
					: error?.message ?? 'Something went wrong creating this pipeline.'
			);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Formik initialValues={initialValues} validationSchema={currentSchema} validateOnChange validateOnBlur onSubmit={handleSubmitStep}>
			{({ values, errors, touched, setFieldValue, setFieldTouched, setValues, submitForm, isSubmitting }) => {
				const isStepValid = currentSchema ? currentSchema.isValidSync(values) : true;

				return (
					<>
						<Stepper activeStep={activeStepIndex} alternativeLabel sx={{ pb: 2, borderBottom: '1px solid #ddd' }}>
							{STEPS.map(step => (
								<Step key={step.key}>
									<StepLabel>{step.label}</StepLabel>
								</Step>
							))}
						</Stepper>

						<Box p={5}>
							{activeStepIndex === 0 && <SourceImageStep values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} />}
							{activeStepIndex === 1 && <TargetContainerStep values={values} errors={errors} touched={touched} setValues={setValues} />}
							{activeStepIndex === 2 && (
								<NameStep
									values={values}
									errors={errors}
									touched={touched}
									setFieldValue={setFieldValue}
									setFieldTouched={setFieldTouched}
									createError={createError}
								/>
							)}
							{activeStepIndex === 3 && createdPipeline && <RunStep pipelineId={createdPipeline.Id} onTriggered={handleRunTriggered} />}
						</Box>

						{activeStepIndex < 3 && (
							<FooterWrapper className="footer" sx={{ '& .MuiButtonBase-root': { minWidth: '80px' } }}>
								<Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
									<Button size="small" onClick={handleCancel}>
										Cancel
									</Button>
									<MobileStepper
										variant="progress"
										steps={3}
										position="static"
										activeStep={activeStepIndex}
										nextButton={
											<Button size="small" onClick={submitForm} disabled={!isStepValid || isSubmitting}>
												{isLastFormikStep ? 'Create' : 'Next'}
												<KeyboardArrowRight />
											</Button>
										}
										backButton={
											<Button size="small" onClick={handleBack} disabled={activeStepIndex === 0}>
												<KeyboardArrowLeft />
												Back
											</Button>
										}
									/>
								</Box>
							</FooterWrapper>
						)}
					</>
				);
			}}
		</Formik>
	);
};

export default React.memo(PipelineWizard);
